import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Link, OutlinedInput, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGlobalStateContext } from '../../context/GlobalStateContext/GlobalStateContext';
import { useSmsSetupContext } from '../../context/SmsSetupContext/SmsSetupContext';
import { dialogLinks } from '../../layout/AppLayout/constants';
import { useIsDesktop } from '../../utils/mediaQueries';
import { isValidSmsCode, stripFormat } from '../../utils/phone-number.helper';
import { ActionButton } from '../Button/Button';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import styles from './styles';
export const SmsSetupStep2 = () => {
    const { t: text } = useTranslation();
    const errorMessage = {
        SET_UP_INVALID_CODE: text('SET_UP_SMS_INVALID_CODE'),
        SET_UP_EXPIRED_CODE: text('SET_UP_SMS_EXPIRED_CODE'),
        SET_UP_WRONG_CODE: text('SET_UP_SMS_WRONG_CODE'),
        SET_UP_DEAFULT: text('SET_UP_SMS_SERVER_ERROR'),
    };
    const { fetchUser, showSnackBar, globalState: { merchant }, } = useGlobalStateContext();
    const [smsCode, setSmsCode] = useState('');
    const [error, setError] = useState('');
    const [inSubmit, setInSubmit] = useState(false);
    const [isResend, setIsResesnd] = useState(false);
    const { handleSmsSetup, smsSetupContextState, setSmsSetupContextState } = useSmsSetupContext();
    const navigate = useNavigate();
    useEffect(() => {
        showSnackBar(text('SET_UP_SMS_CODE_SENT'));
    }, []);
    useEffect(() => {
        if (smsSetupContextState?.apiReply?.success && isResend) {
            showSnackBar(text('SET_UP_SMS_CODE_SENT'));
        }
        if (smsSetupContextState?.apiReply?.success) {
            setSmsSetupContextState({
                ...smsSetupContextState,
                apiReply: {
                    success: false,
                    reason: '',
                },
            });
        }
    }, [smsSetupContextState.apiReply]);
    const resendCode = async () => {
        setIsResesnd(true);
        try {
            await handleSmsSetup({
                isResend: true,
                phoneNumber: JSON.parse(localStorage.getItem('sms-setup') || '').phoneNumber,
            }, 0);
        }
        catch (e) {
            console.log('SMS setup error ', e);
        }
    };
    const handleOnBlur = (e) => {
        setIsResesnd(false);
        const smsCodeValue = e.target.value;
        if (isValidSmsCode(stripFormat(smsCodeValue))) {
            setSmsCode(smsCodeValue);
            setError('');
        }
        else {
            setError(errorMessage.SET_UP_INVALID_CODE);
        }
        setInSubmit(false);
    };
    const handleOnChange = (e) => {
        setIsResesnd(false);
        setError('');
        setInSubmit(false);
        const inputValue = e.target.value;
        let numberString = stripFormat(inputValue);
        if (numberString.length > 6)
            numberString = numberString.slice(0, 6);
        if (isNaN(Number(numberString)))
            return;
        setSmsCode(numberString);
    };
    const goBack = async () => {
        setIsResesnd(false);
        setSmsSetupContextState({
            ...smsSetupContextState,
            smsSetupCurrentStep: 1,
        });
    };
    const handleSubmit = async () => {
        setIsResesnd(false);
        if (!smsCode || error) {
            setError(errorMessage.SET_UP_INVALID_CODE);
            return false;
        }
        setInSubmit(true);
        try {
            handleSmsSetup({
                verificationCode: smsCode,
            }, 1, false);
        }
        catch (e) {
            console.log('SMS setup error', e);
        }
    };
    useEffect(() => {
        if (smsSetupContextState?.apiReply?.reason) {
            switch (smsSetupContextState?.apiReply?.failureCode) {
                case 'ERR_102':
                    setError(errorMessage.SET_UP_WRONG_CODE);
                    break;
                case 'ERR_101':
                    setError(errorMessage.SET_UP_EXPIRED_CODE);
                    break;
            }
        }
        if (smsSetupContextState?.apiReply?.success && inSubmit) {
            const fetchData = async () => {
                try {
                    await fetchUser();
                }
                catch (e) {
                    console.log(e);
                }
            };
            fetchData();
        }
    }, [smsSetupContextState]);
    useEffect(() => {
        if (merchant?.notifOnboardingStatus && inSubmit) {
            navigate(dialogLinks.ONBOARDING_COMPLETED);
        }
    }, [merchant]);
    const isDesktop = useIsDesktop();
    return (_jsxs(Box, { sx: styles.pageContentWrapper, children: [isDesktop && (_jsxs(Box, { sx: { flex: '0' }, children: [_jsx(Typography, { variant: 'h1', sx: { mt: '2rem' }, children: text('SET_UP_SMS_VERIFY_TITLE') }), _jsx(Box, { sx: styles.subtitle, children: _jsx(Box, { children: _jsx(Typography, { variant: 'subtitle1', children: text('SET_UP_SMS_VERIFY_SUBTITLE') }) }) })] })), _jsxs(Box, { children: [_jsx(Typography, { variant: 'h2', sx: { mb: '0.5rem', mt: '1.5rem' }, children: text('SET_UP_SMS_ENTER_CODE') }), _jsx(Typography, { mb: 1, variant: 'body2', children: text('SET_UP_SMS_10_MINUTES') })] }), _jsxs(Box, { mb: 2, children: [_jsx("label", { htmlFor: 'smsCode', children: _jsx(Typography, { mb: 1, variant: 'body2', children: text('SET_UP_SMS_CODE') }) }), _jsx(OutlinedInput, { value: smsCode, error: false, fullWidth: true, onChange: handleOnChange, onBlur: handleOnBlur, sx: { background: 'white' }, name: 'smsCode', id: 'smsCode' }), _jsx(Box, { children: error && _jsx(ErrorMessage, { type: 'Plain', message: error }) }), _jsxs(Typography, { mb: 1, variant: 'body2', mt: 1, children: [text('SET_UP_SMS_NO_CODE'), _jsxs(Link, { onClick: () => {
                                    resendCode();
                                }, sx: { cursor: 'pointer' }, tabIndex: 0, onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        resendCode();
                                    }
                                }, children: [' ', text('SET_UP_SMS_SEND_AGAIN')] })] })] }), _jsxs(Box, { sx: styles.step2CTAWrapper, children: [_jsx(ActionButton, { type: 'secondary', text: text('SET_UP_SMS_GO_BACK'), onClick: () => goBack() }), _jsx(ActionButton, { type: 'primary', text: text('CONTINUE'), disabled: inSubmit, onClick: () => {
                            handleSubmit();
                        } })] })] }));
};
